import { useCallback, useEffect, useRef, useState } from "react";
import type { PlayerAction, RoomView, Trade } from "@grundbesitz/shared";
import { emptyTradeSide, mirrorTrade, sortTradesNewestFirst } from "../../lib/assets";
import { formatMoney } from "../../lib/format";
import { useStore } from "../../lib/store";
import { Button } from "../ui/Button";
import { useAct } from "./assets/useAct";
import { TradeEditor } from "./trade/TradeEditor";
import type { EditorSeed } from "./trade/TradeEditor";
import { TradeOfferCard } from "./trade/TradeOfferCard";
import { useUiStore } from "./uiStore";

/** Trading: incoming and own offers, and the editor for new offers and counter offers. */
export function TradePanel({ room }: { room: RoomView }) {
  const game = room.game;
  const meId = room.you.playerId;
  const me = game && meId ? game.players.find((p) => p.id === meId) : undefined;
  const { busy, run } = useAct();
  const tradeTarget = useUiStore((s) => s.tradeTarget);
  const setTradeTarget = useUiStore((s) => s.setTradeTarget);
  const [editor, setEditor] = useState<EditorSeed | null>(null);
  const seedCounter = useRef(0);
  const [notices, setNotices] = useState<{ id: number; text: string }[]>([]);
  const roomRef = useRef(room);
  roomRef.current = room;

  const openEditor = useCallback((partial: Partial<Omit<EditorSeed, "key">> = {}) => {
    setEditor({ key: ++seedCounter.current, to: null, give: emptyTradeSide(), get: emptyTradeSide(), counterOf: null, ...partial });
  }, []);

  // "Handel anbieten" in the player list preselects the partner.
  useEffect(() => {
    if (!tradeTarget) return;
    openEditor({ to: tradeTarget });
    setTradeTarget(null);
  }, [tradeTarget, setTradeTarget, openEditor]);

  // Offers vanish from `game.trades` by themselves when their assets change. Say why.
  useEffect(
    () =>
      useStore.getState().subscribeEvents((batch) => {
        const current = roomRef.current;
        const mine = current.you.playerId;
        for (const ev of batch.events) {
          if (ev.type !== "TRADE_INVALIDATED" || (ev.from !== mine && ev.to !== mine)) continue;
          const nameOf = (id: string) => current.game?.players.find((p) => p.id === id)?.name ?? "Unbekannt";
          const text =
            ev.from === mine
              ? `Dein Angebot an ${nameOf(ev.to)} ist ungültig geworden, weil sich Besitz, Gebäude oder Freikarten geändert haben.`
              : `Das Angebot von ${nameOf(ev.from)} ist ungültig geworden, weil sich Besitz, Gebäude oder Freikarten geändert haben.`;
          const id = ++seedCounter.current;
          setNotices((list) => [...list.slice(-2), { id, text }]);
          window.setTimeout(() => setNotices((list) => list.filter((n) => n.id !== id)), 15000);
        }
      }),
    [],
  );

  if (!game || !meId || !me || room.you.spectator || me.eliminated) {
    return (
      <p className="rounded-xl border border-dashed border-line p-4 text-sm text-ink-2">
        {me?.eliminated
          ? "Du bist ausgeschieden und schaust jetzt zu. Handeln können nur Spieler, die noch im Spiel sind."
          : "Du schaust zu. Handeln können nur Spieler im Spiel."}
      </p>
    );
  }

  const incoming = sortTradesNewestFirst(game.trades.filter((t) => t.to === meId));
  const outgoing = sortTradesNewestFirst(game.trades.filter((t) => t.from === meId));

  const accept = (t: Trade) => void run(`accept-${t.id}`, { type: "ACCEPT_TRADE", tradeId: t.id });
  const reject = (t: Trade) => void run(`reject-${t.id}`, { type: "REJECT_TRADE", tradeId: t.id });
  const cancel = (t: Trade) => void run(`cancel-${t.id}`, { type: "CANCEL_TRADE", tradeId: t.id });
  const counter = (t: Trade) => openEditor({ to: t.from, counterOf: t.id, ...mirrorTrade(t) });
  const demand = async (t: Trade, extra: number, action: PlayerAction) => {
    const ack = await run(`demand-${t.id}`, action);
    const partner = game.players.find((p) => p.id === t.from)?.name ?? "Unbekannt";
    if (ack.ok) useStore.getState().toast(`Du forderst ${formatMoney(extra)} mehr von ${partner}.`, "success");
  };

  const renderList = (list: Trade[], isIncoming: boolean) => (
    <ul className="space-y-2.5">
      {list.map((t) => (
        <TradeOfferCard
          key={t.id}
          game={game}
          members={room.members}
          trade={t}
          meId={meId}
          incoming={isIncoming}
          busy={busy}
          onAccept={accept}
          onReject={reject}
          onCounter={counter}
          onDemand={(t, extra, action) => void demand(t, extra, action)}
          onCancel={cancel}
        />
      ))}
    </ul>
  );

  return (
    <div className="space-y-5">
      {notices.length > 0 && (
        <div role="status" className="space-y-1.5">
          {notices.map((n) => (
            <p key={n.id} className="rounded-lg border border-warn/70 px-3 py-2 text-sm">
              {n.text}
            </p>
          ))}
        </div>
      )}

      <section aria-labelledby="trade-in" className="space-y-2">
        <h3 id="trade-in" className="font-display text-base font-semibold">
          Angebote an dich{incoming.length > 0 ? ` (${incoming.length})` : ""}
        </h3>
        {incoming.length === 0 ? <p className="text-sm text-ink-2">Im Moment bietet dir niemand etwas an.</p> : renderList(incoming, true)}
      </section>

      <section aria-labelledby="trade-out" className="space-y-2">
        <h3 id="trade-out" className="font-display text-base font-semibold">
          Deine offenen Angebote{outgoing.length > 0 ? ` (${outgoing.length})` : ""}
        </h3>
        {outgoing.length === 0 ? <p className="text-sm text-ink-2">Du hast kein Angebot offen.</p> : renderList(outgoing, false)}
        <p className="text-xs text-ink-2">
          Ein Angebot verfällt von selbst, wenn sich Besitz, Gebäude oder Freikarten der Beteiligten ändern.
        </p>
      </section>

      {editor ? (
        <TradeEditor key={editor.key} room={room} seed={editor} onClose={() => setEditor(null)} />
      ) : (
        <Button variant="primary" className="w-full" onClick={() => openEditor()}>
          Neues Angebot
        </Button>
      )}
    </div>
  );
}
